/* global Office, Word */
import { getAccessToken, getApiBase } from "../../shared/authService";

const WORD_SEARCH_LIMIT = 150;

export type StreamStatus = "ready" | "loading" | "error";

export interface ReviewJurisdiction {
  primary?: string;
  confidence?: string;
  basis?: string;
}

export interface ReviewSuggestionPayload {
  section: string;
  type?: string;
  original: string;
  revised: string;
  reason?: string;
  jurisdictionRelevance?: string;
}

export interface ReviewStreamResult {
  summary?: string;
  jurisdiction?: ReviewJurisdiction;
  suggestions: ReviewSuggestionPayload[];
}

export interface ReviewStreamCallbacks {
  onStatus?: (status: StreamStatus, message?: string) => void;
  onThinking?: (text: string) => void;
  onProgress?: (section: string, type?: string, count?: number) => void;
  onComplete?: (result: ReviewStreamResult) => void;
  onError?: (error: Error) => void;
}

const normalizeWhitespace = (text: string) => text.replace(/[\t\r\n]+/g, " ").replace(/\s+/g, " ").trim();
const getWords = (text: string) => normalizeWhitespace(text).split(" ").filter(Boolean);
const getFirstNWords = (text: string, n: number) => getWords(text).slice(0, n).join(" ");
const getLastNWords = (text: string, n: number) => getWords(text).slice(-n).join(" ");

let officeReady = false;
let officeReadyPromise: Promise<boolean> | null = null;

const ensureOfficeReady = () => {
  if (officeReadyPromise) return officeReadyPromise;
  if (typeof Office === "undefined") {
    officeReadyPromise = Promise.resolve(false);
    return officeReadyPromise;
  }
  officeReadyPromise = new Promise<boolean>((resolve) => {
    Office.onReady((info) => {
      officeReady = info?.host === Office.HostType.Word;
      resolve(officeReady);
    });
  });
  return officeReadyPromise;
};

export const getDocumentText = async (): Promise<string> => {
  await ensureOfficeReady();
  if (!officeReady) {
    throw new Error("Word is not available. Please open this add-in inside Word with a document.");
  }

  try {
    const text = await Word.run(async (context) => {
      const body = context.document.body;
      body.load("text");
      await context.sync();
      return normalizeWhitespace(body.text);
    });

    if (!text || text.trim().length < 10) {
      throw new Error("Document is empty or too short.");
    }

    return text;
  } catch (error) {
    const err = error instanceof Error ? error : new Error("Unable to read document.");
    throw err;
  }
};

const resolveApiBase = (override?: string) => (override && override.trim().length > 0 ? override : getApiBase() || "https://localhost:3000");
const buildUrl = (path: string, apiBase?: string) => {
  const base = resolveApiBase(apiBase).replace(/\/$/, "");
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
};

export interface StreamingRequestOptions {
  prompt: string;
  documentText?: string;
  apiBase?: string;
  callbacks?: ReviewStreamCallbacks;
  signal?: AbortSignal;
}

export const callStreamingReview = async (options: StreamingRequestOptions): Promise<ReviewStreamResult> => {
  const { prompt, documentText, apiBase, callbacks, signal } = options;

  callbacks?.onStatus?.("loading", "Reading document...");
  const text = documentText ?? (await getDocumentText());
  if (!text || text.trim().length < 10) {
    const error = new Error("Document is empty or too short. Please add some content to your document.");
    callbacks?.onStatus?.("error", error.message);
    callbacks?.onError?.(error);
    throw error;
  }

  callbacks?.onStatus?.("loading", "Analyzing...");

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildUrl("/api/v1/addin/chats/suggestions-stream/", apiBase), {
    method: "POST",
    headers,
    body: JSON.stringify({ text: text, prompt: prompt }),
    signal,
  });

  if (!response.ok || !response.body) {
    const error = new Error("Streaming API request failed");
    callbacks?.onStatus?.("error", error.message);
    callbacks?.onError?.(error);
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";
  let finalResult: ReviewStreamResult | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7).trim();
          continue;
        }

        if (line.startsWith("data: ")) {
          const payload = JSON.parse(line.slice(6));

          if (currentEvent === "text" || currentEvent === "thinking") {
            if (payload?.text) {
              callbacks?.onThinking?.(payload.text);
            }
          } else if (currentEvent === "progress") {
          callbacks?.onProgress?.(payload.section, payload.type, payload.count);
          } else if (currentEvent === "complete") {
            finalResult = {
              summary: payload.summary,
              jurisdiction: payload.jurisdiction,
              suggestions: Array.isArray(payload.suggestions) ? payload.suggestions : [],
            };
            callbacks?.onStatus?.("ready", "Ready");
            callbacks?.onComplete?.(finalResult);
            return finalResult;
          } else if (currentEvent === "error") {
            const error = new Error(payload?.error || "Streaming error");
            callbacks?.onStatus?.("error", error.message);
            callbacks?.onError?.(error);
            throw error;
          }

          currentEvent = "";
        }
      }
    }
  } catch (error: any) {
    const err = error instanceof Error ? error : new Error("Streaming request failed");
    callbacks?.onStatus?.("error", err.message);
    callbacks?.onError?.(err);
    throw err;
  }

  callbacks?.onStatus?.("ready", "Ready");
  return finalResult ?? { suggestions: [] };
};

const findTextRange = async (context: Word.RequestContext, original: string): Promise<Word.Range | null> => {
  const normalized = normalizeWhitespace(original);

  if (normalized.length <= WORD_SEARCH_LIMIT) {
    const results = context.document.body.search(normalized, { matchCase: false, matchWholeWord: false });
    results.load("items");
    await context.sync();
    if (results.items.length > 0) {
      return results.items[0];
    }
  }

  const startPhrase = getFirstNWords(original, 6);
  const endPhrase = getLastNWords(original, 6);

  const startResults = context.document.body.search(startPhrase, { matchCase: false, matchWholeWord: false });
  startResults.load("items");
  await context.sync();
  if (startResults.items.length === 0) return null;

  const endResults = context.document.body.search(endPhrase, { matchCase: false, matchWholeWord: false });
  endResults.load("items");
  await context.sync();
  if (endResults.items.length === 0) {
    return startResults.items[0];
  }

  const expectedLength = normalized.length;
  for (let i = 0; i < startResults.items.length; i++) {
    const startRange = startResults.items[i];
    const startPara = startRange.paragraphs.getFirst();
    startPara.load("text");
    await context.sync();

    for (let j = 0; j < endResults.items.length; j++) {
      const endRange = endResults.items[j];
      const endPara = endRange.paragraphs.getFirst();
      endPara.load("text");
      await context.sync();

      if (normalizeWhitespace(startPara.text) === normalizeWhitespace(endPara.text)) {
        try {
          const fullRange = startRange.expandTo(endRange);
          fullRange.load("text");
          await context.sync();
          const rangeText = normalizeWhitespace(fullRange.text);
          const lengthDiff = Math.abs(rangeText.length - expectedLength);
          if (lengthDiff < expectedLength * 0.3) {
            return fullRange;
          }
        } catch {
          // expandTo can fail; fall through to next pair
        }
      }
    }
  }

  return startResults.items[0];
};

export const applyTextChangeToDocument = async (original: string, revised: string): Promise<boolean> => {
  await ensureOfficeReady();
  if (!officeReady) {
    return false;
  }

  return Word.run(async (context) => {
    const range = await findTextRange(context, original);
    if (!range) {
      return false;
    }

    let textToInsert = revised;
    if (!original.includes("\n") && revised.includes("\n")) {
      textToInsert = revised.replace(/\n+/g, " ").replace(/\s+/g, " ").trim();
    }

    range.insertText(textToInsert, Word.InsertLocation.replace);
    await context.sync();
    return true;
  });
};
